import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import IconSave from '../../components/icons/IconSave';
import language from '../../variables/language';

const BLANK = {
  enabled: false,
  wordSourceMode: 'LAST_LESSON',
  dateRangeDays: 7,
  questionCount: 8,
  useAi: true,
  difficulty: 'MEDIUM',
  optionsPerQuestion: 4,
  autoNaming: true,
  textAnswerRatio: 0,
};

const flattenConfig = (data) => ({
  enabled: !!data.enabled,
  wordSourceMode: data.wordSourceMode || 'LAST_LESSON',
  dateRangeDays: data.dateRangeDays,
  questionCount: data.questionCount,
  useAi: !!data.useAi,
  difficulty: data.difficulty || 'MEDIUM',
  optionsPerQuestion: data.optionsPerQuestion,
  autoNaming: data.autoNaming !== undefined ? !!data.autoNaming : true,
  textAnswerRatio: data.textAnswerRatio !== undefined ? data.textAnswerRatio : 0,
});

function TriggerResult({ result, t }) {
  if (!result) return '';
  return (
    <div className="quiz-gen-config-result">
      <span className="quiz-gen-config-chip">
        <b>{result.eventsTriggered}</b> {t.resultEventsTriggered}
      </span>
      <span className="quiz-gen-config-chip">
        <b>{result.messagesSent}</b> {t.resultMessagesSent}
      </span>
    </div>
  );
}

export default function QuizGenerationConfigPanel() {
  const langRoot = language[language.lang];
  const t = langRoot.quizGenerationConfigPage;

  const [formValues, setFormValues] = useState(BLANK);
  const [initialValues, setInitialValues] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  useEffect(() => {
    axios.get('/api/quiz-generation-config', { withCredentials: true })
      .then((response) => {
        const flat = flattenConfig(response.data);
        setFormValues(flat);
        setInitialValues(flat);
      })
      .catch(() => {});
  }, []);

  const isDirty = useMemo(() => JSON.stringify(formValues) !== JSON.stringify(initialValues), [formValues, initialValues]);

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };
  const handleDiscard = () => setFormValues(initialValues);

  const buildConfigPayload = () => ({
    enabled: formValues.enabled,
    wordSourceMode: formValues.wordSourceMode,
    dateRangeDays: Number(formValues.dateRangeDays),
    questionCount: Number(formValues.questionCount),
    useAi: formValues.useAi,
    difficulty: formValues.difficulty,
    optionsPerQuestion: Number(formValues.optionsPerQuestion),
    autoNaming: formValues.autoNaming,
    textAnswerRatio: Number(formValues.textAnswerRatio),
  });

  const handleSave = () => {
    if (saving) return;
    setSaving(true);
    axios.put('/api/quiz-generation-config', JSON.stringify(buildConfigPayload()), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }).then((response) => {
      const flat = flattenConfig(response.data);
      setFormValues(flat);
      setInitialValues(flat);
    }).catch(() => {}).finally(() => setSaving(false));
  };

  const handleTriggerAuto = () => {
    if (triggering) return;
    setTriggering(true);
    axios.post('/api/quiz-generation/trigger-auto', {}, { withCredentials: true })
      .then((response) => setTriggerResult(response.data))
      .catch(() => {})
      .finally(() => setTriggering(false));
  };

  return (
    <div className="quiz-gen-config-card">
      <label className="quiz-gen-config-toggle-row">
        <input type="checkbox" checked={formValues.enabled} onChange={(e) => handleFieldChange('enabled', e.target.checked)} />
        <span>{t.enabled}</span>
      </label>

      <label className="quiz-gen-config-field">
        <span>{t.wordSourceMode}</span>
        <select value={formValues.wordSourceMode} onChange={(e) => handleFieldChange('wordSourceMode', e.target.value)}>
          <option value="LAST_LESSON">{t.sourceModeLastLesson}</option>
          <option value="DATE_RANGE">{t.sourceModeDateRange}</option>
        </select>
      </label>

      {formValues.wordSourceMode === 'DATE_RANGE' ? (
        <label className="quiz-gen-config-field">
          <span>{t.dateRangeDays}</span>
          <input type="number" min="1" value={formValues.dateRangeDays} onChange={(e) => handleFieldChange('dateRangeDays', e.target.value)} />
        </label>
      ) : (
        ''
      )}

      <label className="quiz-gen-config-field">
        <span>{t.questionCount}</span>
        <input type="number" min="1" value={formValues.questionCount} onChange={(e) => handleFieldChange('questionCount', e.target.value)} />
      </label>

      <label className="quiz-gen-config-field">
        <span>{t.optionsPerQuestion}</span>
        <input type="number" min="2" value={formValues.optionsPerQuestion} onChange={(e) => handleFieldChange('optionsPerQuestion', e.target.value)} />
      </label>

      <label className="quiz-gen-config-field">
        <span>{t.textAnswerRatio}</span>
        <input type="number" min="0" max="100" value={formValues.textAnswerRatio} onChange={(e) => handleFieldChange('textAnswerRatio', e.target.value)} />
      </label>

      <label className="quiz-gen-config-field">
        <span>{t.difficulty}</span>
        <select value={formValues.difficulty} onChange={(e) => handleFieldChange('difficulty', e.target.value)}>
          <option value="EASY">{t.difficultyEasy}</option>
          <option value="MEDIUM">{t.difficultyMedium}</option>
          <option value="HARD">{t.difficultyHard}</option>
        </select>
      </label>

      <label className="quiz-gen-config-toggle-row">
        <input type="checkbox" checked={formValues.useAi} onChange={(e) => handleFieldChange('useAi', e.target.checked)} />
        <span>{t.useAi}</span>
      </label>

      <label className="quiz-gen-config-toggle-row">
        <input type="checkbox" checked={formValues.autoNaming} onChange={(e) => handleFieldChange('autoNaming', e.target.checked)} />
        <span>{t.autoNaming}</span>
      </label>

      <button type="button" className="quiz-gen-config-trigger-btn" disabled={triggering} onClick={handleTriggerAuto}>
        {triggering ? t.triggering : t.triggerAuto}
      </button>
      <TriggerResult result={triggerResult} t={t} />

      {isDirty ? (
        <div className="quiz-gen-config-form-footer">
          <span className="quiz-gen-config-unsaved-hint">{langRoot.unsavedChanges}</span>
          <div className="quiz-gen-config-discard-link" onClick={handleDiscard}>{langRoot.cancel}</div>
          <button type="button" className="quiz-gen-config-save-btn" disabled={saving} onClick={handleSave}>
            <IconSave />
            {saving ? langRoot['Loading ...'] : langRoot.save}
          </button>
        </div>
      ) : (
        ''
      )}
    </div>
  );
}
